<template>
  <div class="organizations-container">
    <!-- Page Header -->
    <div class="page-header">
      <h1>Organizations</h1>
      <div class="header-actions">
        <button 
          v-if="!showCreateOrgForm" 
          @click="showCreateOrgForm = true" 
          class="btn btn-primary"
        >
          <i class="icon-plus"></i> Create Organization
        </button>
      </div>
    </div>

    <div v-if="error" class="error-message">
      {{ error }}
    </div>
    <div v-if="success" class="success-message">
      {{ success }}
    </div>
    
    <!-- Main Content Area -->
    <div class="main-content">
      <!-- Left Panel: Organizations List -->
      <div class="left-panel">
        <div class="panel-header">
          <h3>Organizations</h3>
          <span class="org-count">{{ organizations.length }} organizations</span>
        </div>
        
        <div v-if="organizations.length === 0" class="no-data">
          <div class="empty-state">
            <i class="icon-organizations"></i>
            <h4>No organizations yet</h4>
            <p>Create your first organization to get started</p>
          </div>
        </div>
        
        <div v-else class="orgs-list">
          <div 
            v-for="org in organizations" 
            :key="org.id" 
            class="org-item"
            :class="{ 'active': selectedOrg?.id === org.id }"
            @click="selectOrganization(org)"
          >
            <div class="org-avatar">
              <i class="icon-building"></i>
            </div>
            <div class="org-info">
              <h4>{{ org.name }}</h4>
              <p>{{ org.description || 'No description' }}</p>
              <div class="org-meta">
                <span class="member-count">{{ getOrgMemberCount(org.id) }} members</span>
                <span class="org-status" :class="{ 'active': (org.is_active || org.status === 'active') }">
                  {{ (org.is_active || org.status === 'active') ? 'Active' : 'Inactive' }}
                </span>
              </div>
            </div>
            <div class="org-actions">
              <button @click.stop="editOrganization(org)" class="btn-icon" title="Edit">
                <i class="icon-edit"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Right Panel: Organization Details/Form -->
      <div class="right-panel">
        <!-- Create Organization Form -->
        <div v-if="showCreateOrgForm" class="org-form-panel">
          <div class="panel-header">
            <h3>Create New Organization</h3>
            <button @click="closeOrgForm" class="btn-close">
              <i class="icon-close"></i>
            </button>
          </div>
          
          <form @submit.prevent="createOrganization" class="org-form">
            <div class="form-row">
              <div class="form-group">
                <label>Organization Name *</label>
                <input
                  v-model="orgForm.name"
                  type="text"
                  placeholder="Enter organization name"
                  required
                >
              </div>
              <div class="form-group">
                <label>Industry</label>
                <select v-model="orgForm.industry">
                  <option value="">Select Industry</option>
                  <option value="technology">Technology</option>
                  <option value="healthcare">Healthcare</option>
                  <option value="finance">Finance</option>
                  <option value="education">Education</option>
                  <option value="retail">Retail</option>
                  <option value="manufacturing">Manufacturing</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            
            <div class="form-group">
              <label>Description</label>
              <textarea
                v-model="orgForm.description"
                placeholder="Organization description"
                rows="3"
              ></textarea>
            </div>
            
            <div class="form-actions">
              <button type="button" @click="closeOrgForm" class="btn btn-secondary">
                Cancel
              </button>
              <button type="submit" class="btn btn-primary">
                Create Organization
              </button>
            </div>
          </form>
        </div>
        
        <!-- Edit Organization Form -->
        <div v-else-if="showEditOrgForm" class="org-form-panel">
          <div class="panel-header">
            <h3>Edit Organization</h3>
            <button @click="showEditOrgForm = false" class="btn-close">
              <i class="icon-close"></i>
            </button>
          </div>
          
          <form @submit.prevent="updateOrganization" class="org-form">
            <div class="form-row">
              <div class="form-group">
                <label>Organization Name *</label>
                <input
                  v-model="orgForm.name"
                  type="text"
                  placeholder="Enter organization name"
                  required
                >
              </div>
              <div class="form-group">
                <label>Industry</label>
                <select v-model="orgForm.industry">
                  <option value="">Select Industry</option>
                  <option value="technology">Technology</option>
                  <option value="healthcare">Healthcare</option>
                  <option value="finance">Finance</option>
                  <option value="education">Education</option>
                  <option value="retail">Retail</option>
                  <option value="manufacturing">Manufacturing</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            
            <div class="form-group">
              <label>Description</label>
              <textarea
                v-model="orgForm.description"
                placeholder="Organization description"
                rows="3"
              ></textarea>
            </div>
            
            <div class="form-row">
              <div class="form-group">
                <label>Company Size</label>
                <select v-model="orgForm.size">
                  <option value="">Select Size</option>
                  <option value="1-10">1-10 employees</option>
                  <option value="11-50">11-50 employees</option>
                  <option value="51-200">51-200 employees</option>
                  <option value="201-1000">201-1000 employees</option>
                  <option value="1000+">1000+ employees</option>
                </select>
              </div>
              <div class="form-group">
                <label>Email</label>
                <input
                  v-model="orgForm.email"
                  type="email"
                  placeholder="organization@example.com"
                >
              </div>
            </div>
            
            <div class="form-row">
              <div class="form-group">
                <label>Phone</label>
                <input
                  v-model="orgForm.phone"
                  type="tel"
                  placeholder="+1 (555) 123-4567"
                >
              </div>
              <div class="form-group">
                <label>Website</label>
                <input
                  v-model="orgForm.website"
                  type="url"
                  placeholder="https://example.com"
                >
              </div>
            </div>
            
            <div class="form-group">
              <label>Address</label>
              <textarea
                v-model="orgForm.address"
                placeholder="Organization address"
                rows="2"
              ></textarea>
            </div>
            
            <div class="form-group">
              <label>Timezone</label>
              <select v-model="orgForm.timezone">
                <option value="UTC">UTC</option>
                <option value="America/New_York">Eastern Time</option>
                <option value="America/Chicago">Central Time</option>
                <option value="America/Denver">Mountain Time</option>
                <option value="America/Los_Angeles">Pacific Time</option>
                <option value="Europe/London">London</option>
                <option value="Europe/Paris">Paris</option>
                <option value="Asia/Tokyo">Tokyo</option>
              </select>
            </div>
            
            <div class="checkbox-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="orgForm.allowPublicAccess">
                <span>Allow public access to knowledge bases</span>
              </label>
            </div>
            
            <div class="checkbox-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="orgForm.enableAnalytics">
                <span>Enable usage analytics</span>
              </label>
            </div>
            
            <div class="form-actions">
              <button type="button" @click="showEditOrgForm = false" class="btn btn-secondary">
                Cancel
              </button>
              <button type="submit" class="btn btn-primary">
                Update Organization
              </button>
            </div>
          </form>
        </div>
        
        <!-- Organization Details -->
        <div v-else-if="selectedOrg" class="org-details-panel">
          <div class="panel-header">
            <div class="org-title">
              <h3>{{ selectedOrg.name }}</h3>
              <span :class="['status-badge', (selectedOrg.is_active || selectedOrg.status === 'active') ? 'active' : 'inactive']">
                {{ (selectedOrg.is_active || selectedOrg.status === 'active') ? 'Active' : 'Inactive' }}
              </span>
            </div>
            <div class="header-actions">
              <button @click="editOrganization(selectedOrg)" class="btn btn-outline">
                <i class="icon-edit"></i> Edit
              </button>
              <button @click="deleteOrganization(selectedOrg.id)" class="btn btn-danger">
                <i class="icon-trash"></i> Delete
              </button>
            </div>
          </div>
          
          <!-- Organization Overview -->
          <div class="org-overview">
            <div class="org-stats">
              <div class="stat-item">
                <span class="label">Members:</span>
                <span class="value">{{ getOrgMemberCount(selectedOrg.id) }}</span>
              </div>
              <div class="stat-item">
                <span class="label">Industry:</span>
                <span class="value">{{ selectedOrg.industry || 'Not specified' }}</span>
              </div>
              <div class="stat-item">
                <span class="label">Created:</span>
                <span class="value">{{ formatDate(selectedOrg.created_at) }}</span>
              </div>
              <div class="stat-item">
                <span class="label">Status:</span>
                <span class="value status" :class="{ 'active': (selectedOrg.is_active || selectedOrg.status === 'active') }">
                  {{ (selectedOrg.is_active || selectedOrg.status === 'active') ? 'Active' : 'Inactive' }}
                </span>
              </div>
            </div>
          </div>
          
          <!-- Organization Description -->
          <div class="org-description-section">
            <h4>Description</h4>
            <p>{{ selectedOrg.description || 'No description provided.' }}</p>
          </div>
          
          <!-- Organization Members -->
          <div class="org-members-section">
            <h4>Members ({{ getOrgMemberCount(selectedOrg.id) }})</h4>
            <div class="members-list">
              <div 
                v-for="member in getOrgMembers(selectedOrg.id)" 
                :key="member.id" 
                class="member-item"
              >
                <div class="member-avatar">
                  <i class="icon-user"></i>
                </div>
                <div class="member-info">
                  <h5>{{ member.name || member.email }}</h5>
                  <p>{{ member.email }}</p>
                  <span class="member-role">{{ getRoleName(member.role_id) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Welcome Panel -->
        <div v-else class="org-welcome-panel">
          <div class="welcome-content">
            <i class="icon-organizations"></i>
            <h3>Organization Management</h3>
            <p>Select an organization from the left to view details and manage members, or create a new organization to get started.</p>
            <button @click="showCreateOrgForm = true" class="btn btn-primary">
              <i class="icon-plus"></i> Create Organization
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.organizations-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8f9fa;
  overflow: hidden;
}

.page-header {
  display: flex;
  justify-content: between;
  align-items: center;
  padding: 1.5rem 2rem;
  background: white;
  border-bottom: 1px solid #e9ecef;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.page-header h1 {
  margin: 0;
  color: #2c3e50;
  font-size: 2rem;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
}

.error-message,
.success-message {
  padding: 0.75rem 2rem;
  margin: 0;
  font-weight: 500;
}

.error-message {
  background: #f8d7da;
  color: #721c24;
  border-bottom: 1px solid #f1aeb5;
}

.success-message {
  background: #d1e7dd;
  color: #0f5132;
  border-bottom: 1px solid #a3cfbb;
}

.main-content {
  flex: 1;
  display: flex;
  min-height: 0;
}

.left-panel {
  width: 360px;
  background: white;
  border-right: 1px solid #e9ecef;
  display: flex;
  flex-direction: column;
}

.panel-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
  background: #f8f9fa;
}

.panel-header h3 {
  margin: 0 0 0.5rem;
  color: #2c3e50;
  font-size: 1.25rem;
  font-weight: 600;
}

.org-count {
  color: #6c757d;
  font-size: 0.9rem;
}

.no-data {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
}

.empty-state {
  text-align: center;
  color: #6c757d;
}

.empty-state i {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-state h4 {
  margin: 0 0 0.5rem;
  color: #495057;
}

.empty-state p {
  margin: 0;
  font-size: 0.9rem;
}

.orgs-list {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
}

.org-item {
  display: flex;
  align-items: flex-start;
  padding: 1rem;
  margin-bottom: 0.5rem;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.org-item:hover {
  border-color: #6f42c1;
  box-shadow: 0 2px 8px rgba(111, 66, 193, 0.1);
}

.org-item.active {
  border-color: #6f42c1;
  background: #f8f6ff;
  box-shadow: 0 2px 8px rgba(111, 66, 193, 0.15);
}

.org-avatar {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: linear-gradient(135deg, #6f42c1, #5a2d91);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-right: 1rem;
  flex-shrink: 0;
}

.org-avatar i {
  font-size: 1.25rem;
}

.org-info {
  flex: 1;
  min-width: 0;
}

.org-info h4 {
  margin: 0 0 0.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: #2c3e50;
}

.org-info p {
  margin: 0 0 0.5rem;
  font-size: 0.9rem;
  color: #6c757d;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.org-meta {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.85rem;
}

.member-count {
  color: #6c757d;
}

.org-status {
  background: #ffebee;
  color: #c62828;
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  font-weight: 500;
}

.org-status.active {
  background: #e8f5e8;
  color: #2e7d32;
}

.org-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: 0.5rem;
}

.btn-icon {
  width: 32px;
  height: 32px;
  border: 1px solid #dee2e6;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  transition: all 0.2s ease;
}

.btn-icon:hover {
  border-color: #6f42c1;
  color: #6f42c1;
}

.right-panel {
  flex: 1;
  background: white;
  overflow-y: auto;
}

.org-form-panel {
  padding: 2rem;
}

.org-form-panel .panel-header {
  display: flex;
  justify-content: between;
  align-items: center;
  padding: 0 0 1.5rem;
  border-bottom: 1px solid #e9ecef;
  margin-bottom: 2rem;
  background: none;
}

.btn-close {
  width: 32px;
  height: 32px;
  border: none;
  background: #f8f9fa;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  transition: all 0.2s ease;
}

.btn-close:hover {
  background: #e9ecef;
  color: #495057;
}

.org-form {
  max-width: 600px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.9rem;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 0.95rem;
  transition: border-color 0.2s ease;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #6f42c1;
  box-shadow: 0 0 0 3px rgba(111, 66, 193, 0.1);
}

.checkbox-group {
  margin-bottom: 1.5rem;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.95rem;
  color: #2c3e50;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  width: auto;
  margin: 0;
}

.form-actions {
  display: flex;
  gap: 0.75rem;
  padding-top: 2rem;
  border-top: 1px solid #e9ecef;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.btn-primary {
  background: #6f42c1;
  color: white;
}

.btn-primary:hover {
  background: #5a2d91;
}

.btn-secondary {
  background: #f8f9fa;
  color: #6c757d;
  border: 1px solid #dee2e6;
}

.btn-secondary:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.btn-outline {
  background: white;
  color: #6f42c1;
  border: 1px solid #6f42c1;
}

.btn-outline:hover {
  background: #6f42c1;
  color: white;
}

.btn-danger {
  background: #dc3545;
  color: white;
}

.btn-danger:hover {
  background: #c82333;
}

.org-details-panel {
  padding: 2rem;
}

.org-details-panel .panel-header {
  display: flex;
  justify-content: between;
  align-items: center;
  padding: 0 0 1.5rem;
  border-bottom: 1px solid #e9ecef;
  margin-bottom: 2rem;
  background: none;
}

.org-title {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.org-title h3 {
  margin: 0;
  font-size: 1.5rem;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.status-badge.active {
  background: #e8f5e8;
  color: #2e7d32;
}

.status-badge.inactive {
  background: #ffebee;
  color: #c62828;
}

.org-overview {
  margin-bottom: 2rem;
}

.org-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.stat-item {
  display: flex;
  justify-content: between;
  align-items: center;
}

.stat-item .label {
  font-size: 0.9rem;
  color: #6c757d;
  font-weight: 500;
}

.stat-item .value {
  font-size: 1.1rem;
  color: #2c3e50;
  font-weight: 600;
}

.stat-item .value.status {
  font-size: 0.9rem;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  text-align: center;
  background: #ffebee;
  color: #c62828;
}

.stat-item .value.status.active {
  background: #e8f5e8;
  color: #2e7d32;
}

.org-description-section {
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
}

.org-description-section h4 {
  margin: 0 0 1rem;
  color: #2c3e50;
  font-size: 1.1rem;
  font-weight: 600;
}

.org-description-section p {
  margin: 0;
  color: #6c757d;
  line-height: 1.6;
}

.org-members-section {
  padding: 1.5rem;
  overflow-y: auto;
}

.org-members-section h4 {
  margin: 0 0 1rem;
  color: #2c3e50;
  font-size: 1.1rem;
  font-weight: 600;
}

.members-list {
  display: grid;
  gap: 1rem;
}

.member-item {
  display: flex;
  align-items: center;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.member-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6f42c1, #5a2d91);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-right: 1rem;
  flex-shrink: 0;
}

.member-info {
  flex: 1;
  min-width: 0;
}

.member-info h5 {
  margin: 0 0 0.25rem;
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
}

.member-info p {
  margin: 0 0 0.25rem;
  font-size: 0.85rem;
  color: #6c757d;
}

.member-role {
  background: #f3e8ff;
  color: #6f42c1;
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
}

.org-welcome-panel {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.org-welcome-panel .welcome-content {
  text-align: center;
  color: #6c757d;
  max-width: 500px;
}

.org-welcome-panel .welcome-content i {
  font-size: 5rem;
  margin-bottom: 1.5rem;
  opacity: 0.3;
  color: #6f42c1;
}

.org-welcome-panel .welcome-content h3 {
  margin: 0 0 1rem;
  color: #2c3e50;
  font-size: 1.5rem;
}

.org-welcome-panel .welcome-content p {
  margin: 0 0 2rem;
  line-height: 1.6;
}

/* Icons */
.icon-plus:before { content: '➕'; }
.icon-close:before { content: '✕'; }
.icon-building:before { content: '🏢'; }
.icon-organizations:before { content: '🏢'; }
.icon-edit:before { content: '✏️'; }
.icon-trash:before { content: '🗑️'; }
.icon-user:before { content: '👤'; }

/* Responsive */
@media (max-width: 768px) {
  .main-content {
    flex-direction: column;
  }
  
  .left-panel {
    width: 100%;
    max-height: 300px;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { organizationsApi } from '@/services/api'

export default {
  name: 'OrganizationsView',
  setup() {
    const store = useStore()
    
    // Reactive state
    const error = ref('')
    const success = ref('')
    const showCreateOrgForm = ref(false)
    const showEditOrgForm = ref(false)
    const selectedOrg = ref(null)

    // Organization form
    const orgForm = reactive({
      name: '',
      description: '',
      industry: '',
      size: '',
      email: '',
      phone: '',
      website: '',
      address: '',
      timezone: 'UTC',
      allowPublicAccess: false,
      enableAnalytics: false
    })

    // Computed properties
    const organizations = computed(() => {
      const rawOrgs = store.state.organizations?.items || []
      return Array.isArray(rawOrgs) ? rawOrgs.filter(org => org && typeof org === 'object' && org.id) : []
    })

    const users = computed(() => {
      const rawUsers = store.state.users?.items || []
      return Array.isArray(rawUsers) ? rawUsers.filter(user => 
        user && 
        typeof user === 'object' &&
        (user.id || user._id) && 
        (user.email || user.name)
      ) : []
    })

    // Cache org members fetched from backend (enriched with user_name/user_email)
    const orgMembers = reactive({})

    const roles = computed(() => store.state.roles?.items || [])

    // Methods
    const fetchOrganizations = async () => {
      try {
        await store.dispatch('organizations/fetchAll')
      } catch (err) {
        console.error('Error fetching organizations:', err)
        error.value = 'Failed to load organizations'
        setTimeout(() => error.value = '', 3000)
      }
    }

    const fetchUsers = async () => {
      try {
        await store.dispatch('users/fetchAll')
      } catch (err) {
        console.error('Error fetching users:', err)
      }
    }

    const fetchOrgMembers = async (orgId) => {
      if (!orgId) return
      try {
        const res = await organizationsApi.getMembers(orgId)
        // API returns array directly or {members}
        orgMembers[orgId] = Array.isArray(res) ? res : (res?.members || res?.data || [])
      } catch (err) {
        console.error('Error fetching org members:', err)
        orgMembers[orgId] = []
      }
    }

    const fetchRoles = async () => {
      try {
        await store.dispatch('roles/fetchAll')
      } catch (err) {
        console.error('Error fetching roles:', err)
      }
    }

    const createOrganization = async () => {
      try {
        await store.dispatch('createOrganization', orgForm)
        success.value = 'Organization created successfully!'
        setTimeout(() => success.value = '', 3000)
        
        closeOrgForm()
        await fetchOrganizations()
      } catch (err) {
        console.error('Error creating organization:', err)
        error.value = 'Failed to create organization'
        setTimeout(() => error.value = '', 3000)
      }
    }

    const updateOrganization = async () => {
      try {
        if (!selectedOrg.value) return

        const updates = {
          name: orgForm.name,
          description: orgForm.description,
          industry: orgForm.industry,
          size: orgForm.size,
          email: orgForm.email,
          phone: orgForm.phone,
          website: orgForm.website,
          address: orgForm.address,
          timezone: orgForm.timezone,
          allowPublicAccess: orgForm.allowPublicAccess,
          enableAnalytics: orgForm.enableAnalytics
        }

        await store.dispatch('updateOrganization', { id: selectedOrg.value.id, updates })

        success.value = 'Organization updated successfully!'
        setTimeout(() => success.value = '', 3000)
        showEditOrgForm.value = false
        
        await fetchOrganizations()
      } catch (error) {
        console.error('Error updating organization:', error)
        error.value = 'Failed to update organization.'
        setTimeout(() => error.value = '', 3000)
      }
    }

    const editOrganization = async (org) => {
      try {
        orgForm.name = org.name
        orgForm.description = org.description
        orgForm.industry = org.industry
        orgForm.size = org.size
        orgForm.email = org.email
        orgForm.phone = org.phone
        orgForm.website = org.website
        orgForm.address = org.address
        orgForm.timezone = org.timezone
        orgForm.allowPublicAccess = org.allowPublicAccess
        orgForm.enableAnalytics = org.enableAnalytics

        showEditOrgForm.value = true
        selectedOrg.value = org
      } catch (error) {
        console.error('Error editing organization:', error)
        error.value = 'Failed to load organization data for editing.'
        setTimeout(() => error.value = '', 3000)
      }
    }

    const deleteOrganization = async (orgId) => {
      if (!confirm('Are you sure you want to delete this organization?')) return
      
      try {
        await store.dispatch('deleteOrganization', orgId)
        success.value = 'Organization deleted successfully!'
        setTimeout(() => success.value = '', 3000)
        selectedOrg.value = null
        await fetchOrganizations()
      } catch (err) {
        console.error('Error deleting organization:', err)
        error.value = 'Failed to delete organization'
        setTimeout(() => error.value = '', 3000)
      }
    }

    const selectOrganization = async (org) => {
      selectedOrg.value = org
      showCreateOrgForm.value = false
      await fetchOrgMembers(org?.id || org?._id)
    }

    const closeOrgForm = () => {
      showCreateOrgForm.value = false
      showEditOrgForm.value = false
      
      // Reset form
      orgForm.name = ''
      orgForm.description = ''
      orgForm.industry = ''
      orgForm.size = ''
      orgForm.email = ''
      orgForm.phone = ''
      orgForm.website = ''
      orgForm.address = ''
      orgForm.timezone = 'UTC'
      orgForm.allowPublicAccess = false
      orgForm.enableAnalytics = false
    }

    const getOrgMemberCount = (orgId) => {
      const members = orgMembers[orgId] || []
      return members.length
    }

    const getOrgMembers = (orgId) => {
      return orgMembers[orgId] || []
    }

    const getRoleName = (roleId) => {
      const role = roles.value.find(r => r.id === roleId)
      return role?.name || 'Unknown Role'
    }

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A'
      try {
        return new Date(dateString).toLocaleDateString()
      } catch (e) {
        return 'Invalid Date'
      }
    }

    // Lifecycle
    onMounted(async () => {
      try {
        await Promise.all([
          fetchOrganizations(),
          fetchUsers(),
          fetchRoles()
        ])
        // Preload members for selected org if available
        const orgId = selectedOrg.value?.id || selectedOrg.value?._id
        if (orgId) await fetchOrgMembers(orgId)
      } catch (error) {
        console.error('Error loading data:', error)
        error.value = 'Failed to load data'
        setTimeout(() => error.value = '', 3000)
      }
    })

    return {
      // State
      error,
      success,
      showCreateOrgForm,
      showEditOrgForm,
      selectedOrg,
      orgForm,
      
      // Computed
      organizations,
      users,
      roles,
      
      // Methods
      createOrganization,
      updateOrganization,
      editOrganization,
      deleteOrganization,
      selectOrganization,
      closeOrgForm,
      getOrgMemberCount,
      getOrgMembers,
      getRoleName,
      formatDate,
      orgMembers
    }
  }
}
</script>
